import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import {connect} from 'react-redux';

import {TIMELINE_START, TIMELINE_END} from 'ducks/allocations';
import {DATE_FORMAT_API} from 'utils/datetime';
import {PurposeTimelineGroup} from './components';

import Timeline, {TodayMarker} from 'react-calendar-timeline/lib';
import 'react-calendar-timeline/lib/Timeline.css';

import CheckBox from 'components/CheckBox';

import './styles.css';

const TOOLTIP_NAME = 'roomTypeTooltip';

const KEYS_MAPPING = {
  groupIdKey: 'id',
  groupTitleKey: 'title',
  groupRightTitleKey: 'rightTitle',
  itemIdKey: 'id',
  itemTitleKey: 'title',
  itemDivTitleKey: 'title',
  itemGroupKey: 'group',
  itemTimeStartKey: 'startDate',
  itemTimeEndKey: 'endDate'
};

const TODAY_MARKER_COLOR = '#fe0c00';

const PURPOSES = {
  ['long_stay']: 'Long stay',
  ['short_stay']: 'Short stay',
  ['guest']: 'Guest',
  ['corporate']: 'Corporate',
  ['extended_stay']: 'Extended stay'
};

const STYLES = {
  ['Long stay']: {
    backgroundColor: '#B9E3AA',
    color: '#228100',
    border: '0.1rem solid #5FBA3F',
    borderRadius: '0.3rem',
    fontSize: '1.3rem'
  },
  ['Extended stay']: {
    backgroundColor: '#FFBD5E',
    color: ' #BE6700',
    border: '0.1rem solid #FF7F22',
    borderRadius: '0.3rem',
    fontSize: '1.3rem'
  },
  ['Short stay']: {
    backgroundColor: '#BFBEEF',
    color: '#480BAC',
    border: '0.1rem solid #7B42D7',
    borderRadius: '0.3rem',
    fontSize: '1.3rem'
  },
  ['Guest']: {
    backgroundColor: '#FFB1D7',
    color: '#BB427E',
    border: '0.1rem solid #D277A4',
    borderRadius: '0.3rem',
    fontSize: '1.3rem'
  },
  ['Corporate']: {
    backgroundColor: '#CCE1FF',
    color: '#0070B9',
    border: '0.1rem solid #71A9FC',
    borderRadius: '0.3rem',
    fontSize: '1.3rem'
  }
};

class PurposesCalendar extends React.Component<any> {
  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  onTimeChange = (visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
    /*
     * <Timeline> receives onTimeChange prop to limit the calendar scroll manually.
     *
     * The function implementation is copy-pasted from react-calendar-timeline documentation
     * https://github.com/namespace-ee/react-calendar-timeline#ontimechangevisibletimestart-visibletimeend-updatescrollcanvas
    */
    if (visibleTimeStart < TIMELINE_START && visibleTimeEnd > TIMELINE_END)
      return updateScrollCanvas(TIMELINE_START, TIMELINE_END);

    if (visibleTimeStart < TIMELINE_START)
      return updateScrollCanvas(
        TIMELINE_START,
        TIMELINE_START + (visibleTimeEnd - visibleTimeStart)
      );

    if (visibleTimeEnd > TIMELINE_END)
      return updateScrollCanvas(
        TIMELINE_END - (visibleTimeEnd - visibleTimeStart),
        TIMELINE_END
      );

    return updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
  };

  purposeGroupRenderer = ({group, isRightSidebar}) => {
    //We save isSelected in store in order to re-render group when we select room.
    //Because there should be a difference between current and past group state or group won`t be re-rendered.
    return (
      <div>
        {!isRightSidebar && (
          <PurposeTimelineGroup
            group={group}
            selectRoom={this.props.selectRoom}
            selectedRoomsIds={this.props.selectedRoomsIds}
          />
        )}
      </div>
    );
  };

  getTimelineStart = () => {
    const {filterStartDate} = this.props;

    if (!_.isNil(filterStartDate)) {
      // `isoWeek` sets the start of the week according to ISO 8601 (will be timezone independent)
      return moment(filterStartDate, DATE_FORMAT_API).startOf('isoWeek');
    }

    return moment().add(-1, 'day');
  };

  getTimelineEnd = () => {
    const {filterEndDate} = this.props;

    if (!_.isNil(filterEndDate)) {
      // `isoWeek` sets the end of the week according to ISO 8601 (will be timezone independent)
      return moment(filterEndDate, DATE_FORMAT_API).endOf('isoWeek');
    }

    return moment().add(29, 'day');
  };

  onChange = event => {
    if (event.target.checked) {
      return this.props.selectAllRooms();
    }
    return this.props.deselectAllRooms();
  };

  roomPurposeLeftSidebarHeaderContent = (
    <div className="roomPurposeSidebarContent">
      <CheckBox
        name="checkAll"
        defaultChecked={false}
        label=""
        onChange={this.onChange}
      />
      <div className="room colab-bold">Room</div>
      <div className="type colab-bold">Type</div>
    </div>
  );

  getItemStyles = ({item, itemContext}) => {
    let customItemStyles = STYLES[PURPOSES[item.purpose]];

    if (itemContext.selected) {
      customItemStyles = STYLES['selected'];
    }

    return {
      ...customItemStyles,
      borderRadius: '0.3rem',
      textAlign: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      justifyContent: 'spaceBetween'
    };
  };

  getItemTitle = item => {
    return PURPOSES[item.purpose];
  };

  itemRenderer = ({item, getItemProps, itemContext}) => (
    <div {...getItemProps({style: this.getItemStyles({item, itemContext})})}>
      {this.getItemTitle(item)}
    </div>
  );

  render() {
    const timelineStart = this.getTimelineStart();
    const timelineEnd = this.getTimelineEnd();
    const {roomPurposesTimeline} = this.props;

    return (
      <div className="PurposesCalendar">
        <Timeline
          lineHeight={40}
          groups={roomPurposesTimeline.groups}
          itemTouchSendsClick={false}
          items={roomPurposesTimeline.items}
          keys={KEYS_MAPPING}
          sidebarWidth={250}
          sidebarContent={this.roomPurposeLeftSidebarHeaderContent}
          canMove={false}
          canChangeGroup={false}
          defaultTimeStart={timelineStart}
          defaultTimeEnd={timelineEnd}
          onTimeChange={this.onTimeChange}
          itemRenderer={this.itemRenderer}
          canResize={false}
          groupRenderer={this.purposeGroupRenderer}>
          <TodayMarker>
            {/* TodayMarker ref: https://github.com/namespace-ee/react-calendar-timeline#todaymarker */}
            {({styles}) => (
              <div style={{...styles, background: TODAY_MARKER_COLOR}} />
            )}
          </TodayMarker>
        </Timeline>
        <ReactTooltip
          place="right"
          delayShow={300}
          effect="solid"
          id={TOOLTIP_NAME}
        />
      </div>
    );
  }
}

export default connect(null, null)(PurposesCalendar);
